import React, { useEffect, useState } from 'react';
import { getInstitucionActiva, actualizarBranding, subirLogo } from '../../services/institucionesService';
import { useInstitucion } from '../../context/InstitucionContext';
import InstitucionBrandingForm, {
  VALORES_BRANDING_VACIOS,
  valoresDesdeInstitucion,
  validarTema,
} from '../../components/admin/InstitucionBrandingForm';

// Marca de la institución activa: nombre, textos, colores y logo. Al guardar se vuelve a
// resolver la institución para que el tema del panel y del sitio cambien al instante.
const AdminMiInstitucion = () => {
  const { paleta: colors, recargar } = useInstitucion();

  const [institucion, setInstitucion] = useState(null);
  const [valores, setValores] = useState(VALORES_BRANDING_VACIOS);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    getInstitucionActiva()
      .then((data) => {
        setInstitucion(data);
        setValores(valoresDesdeInstitucion(data));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (nombre, valor) => {
    setMensaje(null);
    setValores((prev) => ({ ...prev, [nombre]: valor }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMensaje(null);

    const errorTema = validarTema(valores.tema);
    if (errorTema) {
      setError(errorTema);
      return;
    }

    const limpiarColor = (v) => (v && v.trim() ? v.trim().toLowerCase() : null);
    const datos = {
      nombre: valores.nombre.trim(),
      nombreCorto: valores.nombreCorto.trim(),
      descripcion: valores.descripcion.trim() || null,
      tituloPagina: valores.tituloPagina.trim() || null,
      colorPrimario: limpiarColor(valores.colorPrimario),
      colorSecundario: limpiarColor(valores.colorSecundario),
      colorAcento: limpiarColor(valores.colorAcento),
      // Se guarda compacto; el formulario lo muestra formateado.
      tema: valores.tema.trim() ? JSON.stringify(JSON.parse(valores.tema)) : null,
    };

    setGuardando(true);
    try {
      await actualizarBranding(datos);
      setMensaje('Cambios guardados. El sitio ya muestra la nueva marca.');
      recargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleLogo = async (e) => {
    const archivo = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!archivo) return;
    setError(null);
    setMensaje(null);
    setSubiendoLogo(true);
    try {
      const { logoUrl } = await subirLogo(archivo);
      setInstitucion((prev) => ({ ...prev, logoUrl }));
      setMensaje('Logo actualizado.');
      recargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubiendoLogo(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Cargando...</div>;
  }

  if (!institucion) {
    return <div className="p-12 text-center text-red-500">{error || 'No se pudo cargar la institución.'}</div>;
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mi institución</h1>
        <p className="text-gray-500 text-sm mt-1">
          Identidad visual y textos del sitio público de <strong>{institucion.nombreCorto}</strong>
          <span className="text-gray-400"> · slug </span>
          <span className="font-mono text-gray-400">{institucion.slug}</span>
        </p>
      </div>

      {(error || mensaje) && (
        <div
          className={`text-sm rounded-lg px-4 py-3 mb-5 border ${
            error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          {error || mensaje}
        </div>
      )}

      {/* Logo */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Logo</h2>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div
            className="w-40 h-24 rounded-lg flex items-center justify-center p-3"
            style={{ background: `linear-gradient(135deg, ${colors.headerGradientStart}, ${colors.headerGradientEnd})` }}
          >
            {institucion.logoUrl ? (
              <img
                src={`${process.env.PUBLIC_URL}${institucion.logoUrl}`}
                alt="Logo actual"
                className="max-h-full max-w-full drop-shadow"
              />
            ) : (
              <span className="text-white/70 text-xs">Sin logo</span>
            )}
          </div>
          <div className="flex-1">
            <input
              type="file"
              accept=".svg,.png,image/svg+xml,image/png"
              onChange={handleLogo}
              disabled={subiendoLogo}
              className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <p className="text-xs text-gray-400 mt-2">
              SVG o PNG, máximo 2 MB. Se muestra sobre el degradado del encabezado, idealmente en blanco o con fondo transparente.
              {subiendoLogo && ' Subiendo…'}
            </p>
          </div>
        </div>
      </section>

      {/* Datos y colores */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <InstitucionBrandingForm valores={valores} onChange={handleChange} colorRing={colors.primary} />

        {institucion.dominios && institucion.dominios.length > 0 && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Dominios</h3>
            <p className="text-xs text-gray-400 mb-2">Direcciones desde las que se entra directamente a esta institución.</p>
            <ul className="flex flex-wrap gap-2">
              {institucion.dominios.map((d) => (
                <li key={d.id} className={`px-2.5 py-1 rounded-full text-xs font-mono ${d.activo ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400 line-through'}`}>
                  {d.host}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-3 pt-6">
          <button
            type="submit"
            disabled={guardando}
            className="px-5 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminMiInstitucion;
